"use client";
import Link from "next/link";
import { cx } from "lib/cx";
import { useState, useEffect } from "react";
import {
  Bars3Icon,
  DocumentTextIcon,
  PencilSquareIcon,
  PhoneArrowDownLeftIcon,
} from "@heroicons/react/24/outline";
import { ChevronRightIcon } from "@heroicons/react/24/outline";
import TemplatesIcon from "public/assets/templatesIcon.svg";
import MobileDashboardIcon from "public/assets/mobileDashboardIcon.svg";
import Image from "next/image";
import {
  setAccessToken as changeAccessToken,
  selectIsAuthenticationOpen,
  setIsAuthenticationOpen,
} from "lib/redux/uiSlice";
import {
  useAppDispatch,
  useAppSelector,
  useSaveStateToLocalStorageOnChange,
  useSetInitialStore,
} from "lib/redux/hooks";
import { usePathname } from "next/navigation";
import { Button } from "components/Button";
import CustomIcon from "components/CustomIcon";
import CheckScoreIcon from "public/icons/getResumeScore.svg";

const MobileNavbar = ({ onClose, handleAuthClick, accessToken }: any) => {
  return (
    <div className="relative translate-x-0 transform rounded-2xl bg-white p-2 text-[10px] shadow-2xl transition-transform duration-300">
      <button
        onClick={onClose}
        className="absolute -left-2 -top-1 rounded-full bg-[#4E4E4E] p-[2px]"
      >
        <ChevronRightIcon className="h-4 w-4 text-white" />
      </button>
      <ul className="flex flex-col items-start font-montserrat">
        {accessToken && (
          <li>
            <Link href="/dashboard" className="flex items-center gap-2">
              <MobileDashboardIcon /> Dashboard
            </Link>
          </li>
        )}
        <li>
          <Link href="/resume-builder" className="flex items-center gap-2">
            <TemplatesIcon /> Templates
          </Link>
        </li>
        <li>
          <button
            onClick={(e) => {
              e.preventDefault();
              window.location.href = "/upload-resume";
            }} 
            className="flex items-center gap-2"
          >
            <Image
              height={13}
              width={13}
              src="/icons/getResumeScore.svg"
              alt="Score Check"
              className="h-3 w-3"
            />
            Score Check
          </button>
        </li>
        <li>
          <Link href="/referral" className="flex items-center gap-2">
            <PencilSquareIcon className="h-3 w-3" />Referral
          </Link>
        </li>
        <li>
          <Link href="/resume-writing" className="flex items-center gap-2">
            <PencilSquareIcon className="h-3 w-3" /> Resume Writing
          </Link>
        </li>
        <li>
          <Link
            href="/resume-writing/consultation"
            className="flex items-center gap-2"
          >
            <PhoneArrowDownLeftIcon className="h-3 w-3" /> Consultation
          </Link>
        </li>
        <li>
          <Link
            href="/blog"
            className="flex items-center gap-2"
          >
            <DocumentTextIcon className="h-3 w-3" /> Blogs
          </Link>
        </li>
      </ul>
      <button
        className="btn-gradient mt-1 px-8 py-0 font-poppins"
        onClick={handleAuthClick}
      >
        {accessToken ? "Sign out" : "Sign in"}
      </button>
    </div>
  );
};
interface NavbarProps {}

export const Navbar = ({}: NavbarProps) => {
  useSetInitialStore();
  useSaveStateToLocalStorageOnChange();
  const isAuthenticationOpen = useAppSelector(selectIsAuthenticationOpen);

  const pathname = usePathname();
  const [activeTab, setActiveTab] = useState("Home");
  const [isMobileNavVisible, setMobileNavVisible] = useState(false);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (pathname === "/") {
      setActiveTab("Home");
    } else if (pathname === "/resume-builder") {
      setActiveTab("Templates");
    } else if (pathname === "/dashboard") {
      setActiveTab("Dashboard");
    } else if (pathname === "/resume-writing") {
      setActiveTab("ResumeWriting");
    } else if (pathname === "/resume-writing/consultation") {
      setActiveTab("Consultation");
    } 
    else if (pathname === "/affiliate") {
      setActiveTab("Dashboard");
    }
    else if (pathname === "/referral") {
      setActiveTab("Referral");
    }
    else if (pathname === "/blog" || pathname.startsWith("/blog/")) {
      setActiveTab("Blogs");
    }
    else if (pathname === "/score-check" || pathname === "select-experience" || pathname==="/upload-resume" || pathname.startsWith("/score-check/")) {
      setActiveTab("ScoreCheck");
    }
  });

  useEffect(() => {
    const token = localStorage.getItem("access-token");
    setAccessToken(token);
  }, []);

  const handleMobileNavToggle = () => {
    setMobileNavVisible(!isMobileNavVisible);
  };

  const handleCloseMobileNav = () => {
    setMobileNavVisible(false);
  };

  const handleAuthClick = () => {
    if (accessToken) {
      localStorage.removeItem("access-token");
      dispatch(changeAccessToken(null));
      if (pathname === "/dashboard") {
        window.location.href = "/";
      } else if (pathname.startsWith("/resume-writing")) {
        window.location.href = "/resume-writing";
      } else {
        window.location.href = "/";
      }
    } else {
      dispatch(setIsAuthenticationOpen(true));
    }
  };

  return (
    <header
      aria-label="Site Header"
      className={cx(
        "fade-in-navbar sticky top-5 z-50 mx-4 mb-5 flex h-[60px] items-center rounded-2xl bg-white px-6 text-base xl:text-lg shadow-sm shadow-[#0000000D] md:mx-8",
        "relative"
      )}
    >
      <div className="relative flex h-10 w-full items-center justify-between">
        <div className="flex items-center gap-24">
          <Link href="/">
            <span className="sr-only">ResumeMate</span>
            <div className="flex items-center gap-1 lg:gap-2">
              <div className="relative h-[28px] w-[24px] md:h-[35px] md:w-[30px] lg:h-[42px] lg:w-[36px] 2xl:h-[51px] 2xl:w-[44px]">
                <Image
                  src="/assets/resumemateLogo.png"
                  alt="ResumeMate"
                  fill={true}
                  priority
                  fetchPriority="high"
                  sizes="40px"
                />
              </div>
              <div className="font-montserrat text-[10px] font-semibold leading-3 md:text-xs md:leading-3 lg:text-xs lg:leading-3 xl:text-sm xl:leading-4 2xl:text-base 2xl:leading-4">
                <span className="text-[#1C75BC]">Resume</span> <br />
                <span className="text-[#27AAE1]">Mate.io</span>
              </div>
            </div>
          </Link>
        </div>
        <button onClick={handleMobileNavToggle} className="block lg:hidden">
          <Bars3Icon className="h-6 w-6" />
        </button>
        <ul className="hidden gap-7 xl:gap-12 lg:flex">
          <li
            className={`${
              activeTab === "Home"
                ? "font-semibold text-[#52A1FF]"
                : "font-normal"
            }`}
          >
            <Link href="/">Home</Link>
          </li>

          <li className="cursor-pointer">
            <Link href="/resume-builder">Templates</Link>
          </li>
          <li
            className={`${
              activeTab === "ScoreCheck"
                ? "font-semibold text-[#52A1FF]"
                : "font-normal"
            }`}
          >
            <button
              onClick={(e) => {
                e.preventDefault();
                window.location.href = "/upload-resume";
              }}
              className="flex items-center gap-1.5"
            >
              {/* <div className="flex items-center justify-center w-4 h-4">
                <CheckScoreIcon className="w-3.5 h-3.5" />
              </div> */}
              <span>Score Check</span>
            </button>
          </li>
          {accessToken && (
            <li className={`${
              activeTab === "Affiliate"
                ? "font-semibold text-[#52A1FF]"
                : "font-normal"
            }`}>
              <Link href="/dashboard">Dashboard</Link>
            </li>
          )}
          <li
            className={`${
              activeTab === "Referral"
                ? "font-semibold text-[#52A1FF]"
                : "font-normal"
            }`}
          >
            <Link href="/referral">Referral</Link>
          </li>
          <li
            className={`${
              activeTab === "ResumeWriting"
                ? "font-semibold text-[#52A1FF]"
                : "font-normal"
            }`}
          >
            <Link href="/resume-writing">Resume Writing</Link>
          </li>
          <li
            className={`$ {
              activeTab === "Blogs"
                ? "font-semibold text-[#52A1FF]"
                : "font-normal"
            }`}
          >
            <Link href="/blog">Blogs</Link>
          </li>

          {/* <li
            className={`${
              activeTab === "Consultation"
                ? "font-semibold text-[#52A1FF]"
                : "font-normal"
            }`}
          >
            <Link href="/resume-writing/consultation">Consultation</Link>
          </li> */}
        </ul>
        <button
          className="btn-gradient hidden px-8 py-2 font-poppins text-base lg:block"
          onClick={handleAuthClick}
        >
          {accessToken ? "Sign out" : "Sign in"}
        </button>
      </div>
      <div
        className={cx(
          "absolute right-0 top-5 transition-transform duration-500",
          isMobileNavVisible ? "translate-x-0" : "translate-x-[150%]"
        )}
      >
        <MobileNavbar
          onClose={handleCloseMobileNav}
          handleAuthClick={handleAuthClick}
          accessToken={accessToken}
        />
      </div>
    </header>
  );
};
